import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import JsFileDownloader from 'js-file-download';
import Loading from '../table-elements/loading.component';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import editUrl from '../../utils/url-edit.util';
import {
  getQuizDetailBySubscriberId,
  getQuizResultsBySubscriberId,
} from '../../services/quiz.service';
import {
  LIST_QUIZ_ATTEMPTS_RESULTS_ROWS,
  LISTING_COLUMNS_SUBSCRIBER_RESULTS,
} from '../../config/module-configs/quiz.config';
import CELL_DATA_TYPES from '../../config/cell-types.config';
import ROUTE_PATH from '../../config/routes.config';
import { formatDate, formatTimestamp } from '../../utils/datetime.util';

const ViewSubscriberResults = ({ quizId, subId }) => {
  const { id, subscriberId } = useParams;
  const [options, setOptions] = useState({
    totalRows: 0,
    columns: [],
    rows: [],
    reloadCounter: 0,
    error: false,
    loading: true,
    showPagination: false,
    customRows: [],
    showCustomTable: true,
  });

  const [loading, setLoading] = useState(true);
  const [quizDetails, setQuizDetails] = useState({});
  const quizDetailsRef = useRef({});

  const quizSubDetails = () => {
    setLoading(true);
    getQuizDetailBySubscriberId(quizId || id, subId || subscriberId)
      .then((res) => {
        const response = res.data[0];
        setQuizDetails(response);
        quizDetailsRef.current = response;
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const downloadReport = () => {
    getQuizResultsBySubscriberId(quizId || id, subId || subscriberId, 'csv')
      .then((res) => {
        const fileName = `${quizDetails.quizTitle}_${quizDetails.subscriberName}_report`;
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, `${fileName}.csv`);
      })
      .catch(() => {});
  };

  useEffect(() => {
    setLoading(false);
    quizSubDetails();

    getQuizResultsBySubscriberId(quizId || id, subId || subscriberId)
      .then((res) => {
        const initialColumns = [...LISTING_COLUMNS_SUBSCRIBER_RESULTS];

        const quizAttemptColumns = [];
        const quizAttemptResults = [];

        for (let loop = 0; loop < res.data.noOfAttempts; loop += 1) {
          const attempt = {
            id: `attempt${loop + 1}`,
            label: `Attempt ${loop + 1}: ${formatDate(
              res.data.attempts[loop].attemptDate * 1000,
              'll'
            )}`,
            width: `${40 / res.data.noOfAttempts}%`,
            align: 'center',
            dataKey: `attempt${loop + 1}`,
            sortable: false,
            type: CELL_DATA_TYPES.QUIZ_ATTEMPT,
          };
          quizAttemptColumns.push(attempt);
        }

        for (
          let loop = 0;
          loop < LIST_QUIZ_ATTEMPTS_RESULTS_ROWS.length;
          loop += 1
        ) {
          const element = LIST_QUIZ_ATTEMPTS_RESULTS_ROWS[loop];
          element.passingCriteria = Number(
            quizDetailsRef.current.passingCriteria || 0
          );

          for (let loop2 = 0; loop2 < res.data.noOfAttempts; loop2 += 1) {
            if (element.dataKey === 'skippedQuestions') {
              element[`${element.dataKey}${loop2 + 1}`] =
                res.data.attempts[loop2].totalQuestions -
                res.data.attempts[loop2].totalAttempted;
            } else {
              element[`${element.dataKey}${loop2 + 1}`] =
                res.data.attempts[loop2][element.dataKey];
            }

            if (loop2 === 0) {
              element.totalQuestions = res.data.attempts[loop2].totalQuestions;
            }
          }

          quizAttemptResults.push(element);
        }

        initialColumns.splice(1, 0, ...quizAttemptColumns);
        setOptions({
          ...options,
          loading: false,
          rows: res.data.questions,
          columns: initialColumns,
          customRows: quizAttemptResults,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, []);

  return (
    <>
      {loading && <Loading />}

      {!loading && (
        <div>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 2 }}
          >
            <Box>
              <Link href={editUrl(ROUTE_PATH.QUIZ_RESULTS, quizId)}>
                <Button
                  color="inherit"
                  variant="contained"
                  startIcon={<Iconify icon="ic:arrow-back" />}
                >
                  Back
                </Button>
              </Link>
            </Box>
            <Button variant="contained" onClick={downloadReport}>
              Download CSV
            </Button>
          </Stack>

          {quizDetails && (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom>
                    {quizDetails.quizTitle}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {quizDetails.quizDescription}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subscriber Name
                  </Typography>
                  <Typography variant="body1">
                    {quizDetails.subscriberName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Attempts
                  </Typography>
                  <Typography variant="body1">
                    {quizDetails.attempts || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Allowed Attempts
                  </Typography>
                  <Typography variant="body1">
                    {quizDetails.allowedAttempts || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Attempt Time
                  </Typography>
                  <Typography variant="body1">
                    {formatTimestamp(quizDetails.attemptTime, 'lll')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Card>
            <ListData
              actions={[]}
              columns={options.columns}
              rows={options.rows}
              page={options.page}
              rowsPerPage={options.rowsPerPage}
              totalRows={options.totalRows}
              loading={options.loading}
              showPagination={options.showPagination}
              customRows={options.customRows}
              showCustomTable={options.showCustomTable}
            />
          </Card>
        </div>
      )}
    </>
  );
};

ViewSubscriberResults.propTypes = {
  quizId: PropTypes.string.isRequired,
  subId: PropTypes.string.isRequired,
};

export default ViewSubscriberResults;
